// Inventory lifecycle rollout control — EN + TR.
//
// The copy carries the consequence, not the mechanism: turning this on means
// the system may refuse a label when it believes the stock is short.

import type { LocalizedDict } from '../index'

export const ROLLOUT_MODES = ['off', 'allowlist', 'all'] as const
export type RolloutMode = (typeof ROLLOUT_MODES)[number]

type InventoryRolloutStrings = {
  title: string
  description: string
  loading: string
  loadError: string
  status: {
    heading: string
    off: string
    allowlist: (count: number) => string
    all: string
    poBridgeOn: string
    poBridgeOff: string
  }
  readiness: {
    heading: string
    ready: string
    notReady: string
    liveEscrows: string
    staleEscrows: string
    negativeRows: string
    duplicateLines: string
    duplicateReceipts: string
    oldestRecovery: string
    voidUnconfirmed: string
    voidUnconfirmedHint: string
    none: string
    seconds: (value: number) => string
  }
  master: {
    heading: string
    label: string
    hint: string
  }
  mode: {
    heading: string
    hint: string
    off: string
    offHint: string
    allowlist: string
    allowlistHint: string
    all: string
    allHint: string
  }
  cohort: {
    heading: string
    hint: string
    empty: string
    addPlaceholder: string
    add: string
    remove: string
    invalid: string
    duplicate: string
    unknownOrg: string
  }
  poBridge: {
    heading: string
    label: string
    hint: string
  }
  pending: string
  discard: string
  review: string
  reviewTitle: string
  reviewBody: string
  reviewEnable: string
  reviewDisable: string
  confirm: string
  cancel: string
  saving: string
  saved: string
  saveError: string
  help: {
    link: string
    title: string
    intro: string
    statesHeading: string
    offTitle: string
    offBody: string
    onTitle: string
    onBody: string
    readingHeading: string
    readingBody: string
    live: string
    stale: string
    negative: string
    duplicates: string
    recovery: string
    turningHeading: string
    turningOn: string
    turningOff: string
    trapsHeading: string
    trapTtl: string
    trapPoBridge: string
    trapNarrow: string
    close: string
  }
}

export const inventoryRolloutI18n: LocalizedDict<InventoryRolloutStrings> = {
  en: {
    title: 'Inventory rollout',
    description:
      'Whether orders reserve and consume stock. While this is off, stock only changes when someone edits it by hand — approving an order, buying a label and receiving a purchase order all leave it untouched.',
    loading: 'Reading the current rollout…',
    loadError: 'Could not read the current rollout.',
    status: {
      heading: 'Right now',
      off: 'Off — stock does not move on its own',
      allowlist: (count) =>
        count === 1
          ? 'On for 1 organization'
          : `On for ${count} organizations`,
      all: 'On for every organization',
      poBridgeOn: 'Receiving a purchase order adds stock',
      poBridgeOff: 'Receiving a purchase order does not add stock',
    },
    readiness: {
      heading: 'Reconciliation',
      ready: 'Clean',
      notReady: 'Needs attention',
      liveEscrows: 'Live reservations',
      staleEscrows: 'Stale reservations',
      negativeRows: 'Negative stock rows',
      duplicateLines: 'Double-counted order lines',
      duplicateReceipts: 'Double-counted receipts',
      oldestRecovery: 'Oldest unfinished purchase',
      voidUnconfirmed: 'Voided labels the carrier never confirmed',
      voidUnconfirmedHint:
        'These orders cannot buy a new label until the void is confirmed on the order (Purchase attempts → Confirm voided).',
      none: '—',
      seconds: (value) => `${Math.round(value / 60)} min`,
    },
    master: {
      heading: 'Master switch',
      label: 'Orders reserve and consume stock',
      hint: 'With this off nothing below applies, whatever it says.',
    },
    mode: {
      heading: 'Who it applies to',
      hint: 'Start with one organization. Widen once it has run for a while.',
      off: 'Nobody',
      offHint: 'Same as the master switch being off.',
      allowlist: 'Only the organizations listed below',
      allowlistHint: 'The bounded cohort — everyone else keeps the old path.',
      all: 'Everyone',
      allHint: 'Every seller, including external ones.',
    },
    cohort: {
      heading: 'Organizations',
      hint: 'Paste an organization ID. The organization picker has a copy button next to each row.',
      empty: 'No organization listed yet.',
      addPlaceholder: 'Organization ID',
      add: 'Add',
      remove: 'Remove',
      invalid: 'That is not a valid organization ID.',
      duplicate: 'That organization is already listed.',
      unknownOrg: 'Name not found in the directory',
    },
    poBridge: {
      heading: 'Purchase orders',
      label: 'Receiving a purchase order adds stock',
      hint: 'Keep this off until the order side has settled. Re-running quality control on a receipt taken before this was on will add that stock at that moment.',
    },
    pending: 'Unsaved changes',
    discard: 'Discard',
    review: 'Review and apply',
    reviewTitle: 'Apply this rollout?',
    reviewBody:
      'This takes effect immediately, for real orders. If the stock figures are wrong, label purchases will be refused until they are corrected.',
    reviewEnable: 'Turning the lifecycle ON',
    reviewDisable: 'Turning the lifecycle OFF',
    confirm: 'Apply',
    cancel: 'Cancel',
    saving: 'Applying…',
    saved: 'Applied.',
    saveError: 'Could not apply the change.',
    help: {
      link: 'How this works',
      title: 'How the inventory rollout works',
      intro:
        'This switch decides whether an order moves stock on its own, or whether stock only ever changes when someone edits it by hand.',
      statesHeading: 'The two states',
      offTitle: 'While it is off',
      offBody:
        'Stock only changes by hand. Approving an order, buying a label and receiving a purchase order all leave it untouched — so when one unit is left, two orders can both buy a label and the bill is noticed afterwards.',
      onTitle: 'While it is on',
      onBody:
        'Approving an order reserves its stock; buying a label consumes it. If the stock is short the label is refused before the carrier is called — no shipping money is spent. That refusal is the point of the switch.',
      readingHeading: 'Reading the panel',
      readingBody:
        '“Right now” states the rollout in one sentence. “Reconciliation” is the health table; its one-word verdict is enough for a daily glance, and the rows below say where to look when it is not clean.',
      live: 'Live reservations — stock held right now. Not being zero is a sign it is working.',
      stale: 'Stale reservations — a hold left past its life. The sweeper clears these every five minutes, so a number that stays up means something is stuck.',
      negative: 'Negative stock rows — must never happen. If you see one, turn the switch off.',
      duplicates: 'Double-counted order lines or receipts — the same thing counted twice, so demand or stock reads wrong.',
      recovery: 'Oldest unfinished purchase — a label was bought but its record never completed.',
      turningHeading: 'Turning it on and off',
      turningOn:
        'Turn the master switch on, choose who it applies to, add the organization IDs if the cohort is bounded, then Review and apply.',
      turningOff:
        'Turn the master switch off and apply. It takes effect immediately — no deploy. It leaves no reservation stranded, and it does not undo a shipment: what really shipped stays shipped.',
      trapsHeading: 'Three things to know',
      trapTtl:
        'A reservation is held for 24 hours. In a period where no labels are bought, available stock therefore reads lower than the shelf for up to a day before it releases itself.',
      trapPoBridge:
        'Purchase orders are a separate switch. Re-running quality control on a receipt taken while it was off will add that stock at that moment — leave old receipts alone.',
      trapNarrow:
        'Starting with one organization keeps the cost of anything going wrong inside one account.',
      close: 'Got it',
    },
  },
  tr: {
    title: 'Envanter açılışı',
    description:
      'Siparişlerin stoğu ayırıp düşüp düşmediği. Bu kapalıyken stok yalnızca elle değişir — sipariş onaylamak, kargo etiketi almak ve tedarikçi malı teslim almak stoğa dokunmaz.',
    loading: 'Mevcut durum okunuyor…',
    loadError: 'Mevcut durum okunamadı.',
    status: {
      heading: 'Şu an',
      off: 'Kapalı — stok kendi kendine hareket etmiyor',
      allowlist: (count) =>
        count === 1 ? '1 organizasyonda açık' : `${count} organizasyonda açık`,
      all: 'Tüm organizasyonlarda açık',
      poBridgeOn: 'Tedarikçi malı teslim alınınca stok artıyor',
      poBridgeOff: 'Tedarikçi malı teslim alınınca stok artmıyor',
    },
    readiness: {
      heading: 'Mutabakat',
      ready: 'Temiz',
      notReady: 'Bakılması gerekiyor',
      liveEscrows: 'Canlı rezervasyon',
      staleEscrows: 'Takılı rezervasyon',
      negativeRows: 'Eksi stok satırı',
      duplicateLines: 'Çift sayılmış sipariş satırı',
      duplicateReceipts: 'Çift sayılmış teslimat',
      oldestRecovery: 'En eski yarım kalmış satın alma',
      voidUnconfirmed: 'Kargonun onaylamadığı iptal edilmiş etiket',
      voidUnconfirmedHint:
        'Bu siparişler iptal siparişte onaylanana kadar yeni etiket alamaz (Satın alma denemeleri → İptali onayla).',
      none: '—',
      seconds: (value) => `${Math.round(value / 60)} dk`,
    },
    master: {
      heading: 'Ana anahtar',
      label: 'Siparişler stoğu ayırsın ve düşsün',
      hint: 'Bu kapalıyken aşağıdakiler ne yazarsa yazsın geçerli değildir.',
    },
    mode: {
      heading: 'Kimlerde geçerli',
      hint: 'Tek organizasyonla başla. Bir süre koştuktan sonra genişlet.',
      off: 'Hiç kimsede',
      offHint: 'Ana anahtarın kapalı olmasıyla aynı.',
      allowlist: 'Sadece aşağıdaki organizasyonlarda',
      allowlistHint: 'Sınırlı grup — geri kalan herkes eski yolda kalır.',
      all: 'Herkeste',
      allHint: 'Dış satıcılar dahil tüm satıcılar.',
    },
    cohort: {
      heading: 'Organizasyonlar',
      hint: 'Organizasyon kimliğini yapıştır. Organizasyon seçme penceresinde her satırın yanında kopyala düğmesi var.',
      empty: 'Henüz organizasyon eklenmedi.',
      addPlaceholder: 'Organizasyon kimliği',
      add: 'Ekle',
      remove: 'Çıkar',
      invalid: 'Bu geçerli bir organizasyon kimliği değil.',
      duplicate: 'Bu organizasyon zaten listede.',
      unknownOrg: 'Adı dizinde bulunamadı',
    },
    poBridge: {
      heading: 'Tedarikçi siparişleri',
      label: 'Tedarikçi malı teslim alınınca stok artsın',
      hint: 'Sipariş tarafı oturana kadar kapalı kalsın. Bu açılmadan önce alınmış bir teslimatın kalite kontrolünü yeniden koşturursan, o mal o an stoğa girer.',
    },
    pending: 'Kaydedilmemiş değişiklik',
    discard: 'Vazgeç',
    review: 'Gözden geçir ve uygula',
    reviewTitle: 'Bu ayar uygulansın mı?',
    reviewBody:
      'Bu hemen, gerçek siparişler için geçerli olur. Stok rakamları yanlışsa, düzeltilene kadar kargo etiketi alınamaz.',
    reviewEnable: 'Yaşam döngüsü AÇILIYOR',
    reviewDisable: 'Yaşam döngüsü KAPANIYOR',
    confirm: 'Uygula',
    cancel: 'Vazgeç',
    saving: 'Uygulanıyor…',
    saved: 'Uygulandı.',
    saveError: 'Değişiklik uygulanamadı.',
    help: {
      link: 'Nasıl çalışır?',
      title: 'Envanter açılışı nasıl çalışır',
      intro:
        'Bu anahtar, siparişlerin stoğu kendi başına ayırıp düşmesini açar. Kapalıyken stok yalnızca birinin elle değiştirmesiyle değişir.',
      statesHeading: 'İki durum',
      offTitle: 'Kapalıyken',
      offBody:
        'Stok yalnızca elle değişir. Sipariş onaylamak, kargo etiketi almak ve tedarikçi malı teslim almak stoğa dokunmaz — son bir adet kaldığında iki sipariş de etiket alabilir, fatura sonradan fark edilir.',
      onTitle: 'Açıkken',
      onBody:
        'Sipariş onaylanınca stok ayrılır, etiket alınınca düşer. Stok yetmiyorsa etiket alınmaz; kargo parası harcanmadan durur. Anahtarın asıl kazancı bu reddetmedir.',
      readingHeading: 'Paneli okumak',
      readingBody:
        '“Şu an” satırı durumu tek cümleyle söyler. “Mutabakat” bloğu sağlık tablosudur; tepesindeki tek kelimelik hüküm günlük bakış için yeterlidir, temiz değilse aşağıdaki satırlar nereye bakacağını söyler.',
      live: 'Canlı rezervasyon — şu an tutulan stok. Sıfır olmaması çalıştığının işaretidir.',
      stale: 'Takılı rezervasyon — süresi geçtiği hâlde bırakılmamış tutma. Süpürücü beş dakikada bir temizler; sayı inmiyorsa bir şey sıkışmıştır.',
      negative: 'Eksi stok satırı — asla olmamalı. Görürsen anahtarı kapat.',
      duplicates: 'Çift sayılmış sipariş satırı veya teslimat — aynı şey iki kez sayılmış, talep ya da stok yanlış okunuyor.',
      recovery: 'En eski yarım kalmış satın alma — etiket alındı ama kaydı tamamlanmadı.',
      turningHeading: 'Açmak ve kapatmak',
      turningOn:
        'Ana anahtarı aç, kimlerde geçerli olacağını seç, sınırlı tutuyorsan organizasyon kimliğini ekle, sonra “Gözden geçir ve uygula”.',
      turningOff:
        'Ana anahtarı kapat ve uygula. Anında olur, deploy gerekmez. Askıda rezervasyon bırakmaz; gideni de geri almaz — gerçekten gitmiş olan gitmiş sayılır.',
      trapsHeading: 'Bilmen gereken üç şey',
      trapTtl:
        'Rezervasyon 24 saat tutulur. Etiket basılmayan bir dönemde elde görünen stok, kendiliğinden serbest kalana kadar bir gün boyunca raftakinden az görünür.',
      trapPoBridge:
        'Tedarikçi siparişleri ayrı bir anahtardır. O kapalıyken alınmış eski bir teslimatın kalite kontrolünü yeniden koşturursan mal o an stoğa girer — eski teslimatlara dokunma.',
      trapNarrow:
        'Tek organizasyonda başlamak, ters giden bir şeyin zararını tek kaleme hapseder.',
      close: 'Anladım',
    },
  },
}
